import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Timer } from '@landtrustinc/design-system';
import PackageStartingPrice from '../../components/PackagePricingPanel/PackageStartingPrice';
import { formatMoney } from '../../util/currency';
import { isFlexiblePricingEnabled } from '../../util/package';

import {
  NamedLink,
  PrimaryButton,
  PackageAvailabilityNotifyButton,
  EarlyAccessButton,
  CredovaPlugin,
} from '../../components';

import css from './PackagePage.module.css';

const MobileCheckAvailability = ({
  isAvailable,
  outOfBookingRange,
  startingPrice,
  currentTransaction,
  isOnWaitingList,
  setIsPackageAvailabilityModalOpen,
  isBlockedByEarlyAccess,
  earlyAccessExpiration,
  rawParams,
  setIsEarlyAccessModalOpen,
  setIsCredovaModalOpen,
  intl,
  currentUserInit,
  estimatedTotalPrice,
  packageInfo,
  landownerFirstName,
  isOwnListing,
  onMessage,
}) => {
  if (!currentUserInit) {
    return null;
  }

  if (isBlockedByEarlyAccess) {
    return (
      <div className={css.mobileCheckAvailabilityWrapper}>
        <div className={css.mobileCheckAvailability}>
          <div className={css.mobileCheckAvailabilityColumn}>
            <EarlyAccessButton setIsModalOpen={setIsEarlyAccessModalOpen} />

            <div className={css.packageAvailableTimer}>
              <FormattedMessage id="PackagePage.packageAvailable" />
              &nbsp;
              <Timer expirationTimestamp={earlyAccessExpiration} showSeconds fontSize="12px" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Show the waiting list or sold out state when not available or out of booking range
  if (!isAvailable || outOfBookingRange) {
    return (
      <div className={css.mobileCheckAvailabilityWrapper}>
        <div className={css.mobileCheckAvailability}>
          <div className={css.mobileCheckAvailabilityColumn}>
            {isOnWaitingList ? (
              <div className={css.getNotified}>
                <b>
                  <FormattedMessage id="PackagePage.waitingListNotified" />
                </b>
              </div>
            ) : (
              <>
                <PackageAvailabilityNotifyButton
                  onClick={() => {
                    setIsPackageAvailabilityModalOpen(true);
                  }}
                />
                <div className={css.getNotified}>
                  <FormattedMessage id="PackagePricingPanel.soldOutgetNotified" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Without an inquiry action (onMessage), keep the original bar: price and
  // Check availability only. Guests reach the landowner through Buck.
  if (typeof onMessage !== 'function') {
    return (
      <div className={css.mobileCheckAvailabilityWrapper}>
        <div className={css.mobileCheckAvailability}>
          <div className={css.mobileCheckAvailabilityPrice}>
            <span className={css.priceText}>{formatMoney(intl, startingPrice)}</span>
            {startingPrice?.amount && (
              <span className={css.perGuestText}>
                {isFlexiblePricingEnabled(packageInfo.activity) ? (
                  <FormattedMessage id="PackageCard.perAdultHunterText" />
                ) : (
                  <FormattedMessage id="PackageCard.perGuestText" />
                )}
              </span>
            )}
          </div>

          <NamedLink
            name="PricingPackagePage"
            params={{
              ...rawParams,
            }}
            to={{
              state: {
                currentTransaction,
              },
            }}
          >
            <PrimaryButton className={css.checkAvailabilityButton}>
              <FormattedMessage id="PackagePage.checkAvailability" />
            </PrimaryButton>
          </NamedLink>
        </div>

        <div className={css.mobileCredova}>
          <CredovaPlugin
            price={estimatedTotalPrice}
            variant="small"
            onModalOpen={setIsCredovaModalOpen}
          />
        </div>
      </div>
    );
  }

  const quantityBooked = packageInfo?.quantity?.booked || 0;
  const quantityTotal = packageInfo?.quantity?.total || 0;
  const quantityRemaining = quantityTotal - quantityBooked;
  const showSeasonRemaining =
    packageInfo?.hasQuantityRestricted && quantityTotal > 0 && quantityRemaining > 0;
  const messageName = landownerFirstName || 'Landowner';

  // Price sits above Request to book and Message. Request to book uses the
  // same route as the previous Check Availability action.
  return (
    <div className={css.mobileCheckAvailabilityWrapper}>
      <div className={css.mobileFooter}>
        <div className={css.mobileFooterPriceRow}>
          <div className={css.mobileFooterPrice}>
            <PackageStartingPrice
              intl={intl}
              startingPrice={startingPrice}
              priceClassName={css.priceText}
              unitClassName={css.perGuestText}
            />
          </div>
          {showSeasonRemaining && (
            <div className={css.mobileFooterScarcity}>
              <FormattedMessage
                id="PackagePage.seasonRemaining"
                values={{
                  count: quantityRemaining,
                  activity: packageInfo.activity === 'hunt' ? 'hunt' : 'trip',
                }}
              />
            </div>
          )}
        </div>

        <div className={css.mobileFooterActions}>
          <div className={css.mobileFooterAction}>
            <Button
              type="button"
              variant="secondary"
              fullWidth
              className={css.messageLandownerButton}
              onClick={onMessage}
              disabled={isOwnListing}
              data-testid="message-landowner-button"
            >
              <FormattedMessage id="ListingPage.contactLandowner" values={{ name: messageName }} />
            </Button>
          </div>

          <NamedLink
            className={css.mobileFooterAction}
            name="PricingPackagePage"
            params={{
              ...rawParams,
            }}
            to={{
              state: {
                currentTransaction,
              },
            }}
          >
            <PrimaryButton className={css.bookButton} dataTestId="request-to-book-button">
              <FormattedMessage id="PackagePage.requestToBook" />
            </PrimaryButton>
          </NamedLink>
        </div>
      </div>

      <div className={css.mobileCredova}>
        <CredovaPlugin
          price={estimatedTotalPrice}
          variant="small"
          onModalOpen={setIsCredovaModalOpen}
        />
      </div>
    </div>
  );
};

export default injectIntl(MobileCheckAvailability);
